import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { Store } from '@ngrx/store';
import { DropdownMenuWithIconsService } from '@shared/dropdown-menu-with-icons/dropdown-menu-with-icons.service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { AssociationByCandidateIdJobIdObj } from '@app/collections/associations';
import { CandidateHiringManagerStatusObj } from '@app/collections/hiring-manager-status';
import { associationLabelGet } from '@collections/association-label';
import { getAssignsCandidates, isShortListed } from '@modules/assigns/store';
import { CandidateAdapter, CandidateWithAssociation, Job } from '@shared/models';
import { IsMobileViewService } from '@shared/services/is-mobile-view.service';
import { DropdownMenuItem } from 'mojo-abstract-dropdown-menu';

@Component({
  selector: 'mojo-assigns-candidate-container',
  styleUrls: ['./assigned-candidate.container.scss'],
  templateUrl: './assigned-candidate.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedCandidateContainerComponent implements OnInit, OnChanges {
  @Input() assigned: CandidateWithAssociation;
  @Input() expandedView: boolean;
  @Input() job: Job;
  @Input() hiringManagerFlow = false;
  @Input() isWidgetView: boolean;
  @Input() prospectTab = false;

  hover$ = new BehaviorSubject(false);

  isShortListed$: Observable<boolean>;
  isHiringManagerSubmitEnabled$: Observable<boolean>;
  candidates$: Observable<CandidateAdapter[]> = this.store.select(getAssignsCandidates);

  referralExtended = AppFeatureExtensions.REFERRAL_EXTENDED;

  jobMenuItems: DropdownMenuItem[];

  commonMenuItems: DropdownMenuItem[];

  initDropdownMenuOptions() {
    this.jobMenuItems = [
      this.dropdownMenuWithIconsService.getMoveForwardForShortlistForSpecificJob(this.job.id),
      this.dropdownMenuWithIconsService.getMoveForwardForProspectsForSpecificJob(this.job.id),
      this.dropdownMenuWithIconsService.getSubmitToHiringManagerForSpecificJob(this.job.id),
    ];
    this.commonMenuItems = [
      this.dropdownMenuWithIconsService.getInviteToApplyForSpecificJob(this.job.id),
      this.dropdownMenuWithIconsService.getAddAsProspectForAnyJob(),
      this.dropdownMenuWithIconsService.getAddToTalentPoolForAnyJob(),
    ];
  }

  constructor(
    private store: Store<{}>,
    public isMobileViewService: IsMobileViewService,
    private dropdownMenuWithIconsService: DropdownMenuWithIconsService,
  ) {}

  ngOnInit() {
    // this associationLabelGet dispatch is needed for job page widget view to display associations
    if (this.isWidgetView) {
      const props = {
        jobId: this.job.id,
        candidateId: this.assigned.candidate.id,
      };
      if (typeof props.candidateId !== 'number') {
        return;
      }
      this.store.dispatch(associationLabelGet(props));
    }
    this.initDropdownMenuOptions();
  }

  ngOnChanges(changes) {
    if (changes.job || changes.assigned) {
      const props = {
        jobId: this.job.id,
        candidateId: this.assigned.candidate.id,
      };

      this.isShortListed$ = this.store
        .select(
          AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(
            this.assigned.candidate.id,
            this.job.id,
          ),
        )
        .pipe(
          filter((association) => !!association),
          switchMap((association) =>
            this.store.select(isShortListed, {
              jobId: this.job.id,
              phaseId: association.status,
              stateId: association.subStatus,
            }),
          ),
        );

      this.isHiringManagerSubmitEnabled$ = combineLatest([
        this.isShortListed$,
        this.store.select(CandidateHiringManagerStatusObj.getCandidateHiringManagerStatus(props)),
      ]).pipe(
        map(
          ([isShortListedApplicant, candidateJobStatus]) =>
            isShortListedApplicant && !candidateJobStatus,
        ),
      );
    }

    if (changes.job?.currentValue !== changes.job?.previousValue) {
      this.initDropdownMenuOptions();
    }
  }

  @HostListener('mouseenter') onMouseEnter() {
    this.hover$.next(true);
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.hover$.next(false);
  }
}
